import React, { useEffect, useState } from 'react';
import { translate } from '../../i18n';
import { IGENERALCOMENTS } from '../../interfaces/GeneralComments';
import useBgColorStore from '../../store/bgColor';
import useLocationStore from '../../store/locatonStore';
import calenderApi from '../../api/calenderApi';
import { toast } from 'react-toastify';
import { CUSTOM_TOAST_ID } from '../../constants/constant';
import { showOrReplaceToast } from '../../utils/utility';
import ReusableButton from './Button/Button';
import CrossIcon from '../../assets/CrossIcon';
import CheckIcon from '../../assets/CheckIcon';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';
import { encodeDateForUrl, getAdjustedDate } from '../../utils/dateUtils';
import { ICOMMENTS } from '../../interfaces/commentsReq';
import DeleteIcon from '../../assets/DeleteIcon';
import EditIcon from '../../assets/EditIcon';
import operationAPI from '../../api/operationApi';
import { IUpdateComments } from '../../type/updateComment';
import CheckIconDark from '../../assets/CheckIconDark';
import EditIconDark from '../../assets/EditIconDark';
import DeleteIconDark from '../../assets/DeleteIconDark';
import AccordianPlusIcon from '../../assets/AccordianPlusIcon';
import AccordianPlusIconDark from '../../assets/icon/AccordianPlusIconDark';
import Loader from '../../assets/loader/Loader';
import useDateStore from '../../store/selectedDateStore';

const GeneralComment: React.FC<IGENERALCOMENTS> = ({
  showHeading = true,
  date,
}) => {
  const { selectedDateFromStore } = useDateStore();
  const { bgColor } = useBgColorStore();
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const [comments, setComments] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [commentIsLoading, setCommentIsLoading] = useState<boolean>(false);

  const fetchCalendarDetails = async (date: Date) => {
    if (!date) return;
    setLoading(true);
    try {
      setCommentIsLoading(true);
      const ownerId = selectedLocation?.OwnerId;
      const encodedDate = encodeDateForUrl(date);
      const response = await calenderApi.getCalendarDetails(
        ownerId,
        encodedDate,
      );
      const commentsFromApi = response?.data?.general_comments;
      const commentsArray =
        commentsFromApi
          ?.split('\n')
          .map((comment: string) => comment?.trim())
          .filter((comment: string) => comment.length > 0) || [];

      setComments(commentsArray);
      setCommentIsLoading(false);
    } catch (error) {
      setCommentIsLoading(false);
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        'Error fetching calendar details',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarDetails(selectedDateFromStore || getAdjustedDate());
  }, [selectedDateFromStore, selectedLocation.OwnerId]);

  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async () => {
    if (!comment.trim()) {
      setError('Please enter a comment.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const encodedDate = encodeDateForUrl(selectedDateFromStore || new Date());
      const payload: ICOMMENTS = {
        ownerId: selectedLocation?.OwnerId,
        date: encodedDate,
        newComment: comment,
      };
      await calenderApi.addComment(payload);
      showOrReplaceToast(
        'generalComment',
        translate('generalComment.generalCommentAdd'),
        'success',
      );
      setComment('');
      setShowForm(false);
      if (date) fetchCalendarDetails(date);
      setError(null);
    } catch (error) {
      showOrReplaceToast(
        'generalComment',
        translate('generalComment.generalCommentError'),
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCommentChange = (comment: string) => {
    setComment(comment);
  };
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedComment, setEditedComment] = useState<string>('');

  const handleEditClick = (index: number, currentText: string) => {
    setEditingIndex(index);
    setEditedComment(currentText);
    setShowForm(false);
  };

  const handleSaveEdit = async (editingIndex: any) => {
    const encodedDate = encodeDateForUrl(selectedDateFromStore || new Date());
    setShowForm(false);
    const oldComment = comments[editingIndex]; // +1 because slice(1)
    const payload: IUpdateComments = {
      ownerId: selectedLocation?.OwnerId,
      date: encodedDate,
      oldComment,
      newComment: editedComment,
    };
    try {
      setLoading(true);
      await operationAPI.updateComments(payload);
      showOrReplaceToast(
        'generalComment',
        translate('generalComment.generalCommentUpdate'),
        'success',
      );
      fetchCalendarDetails(selectedDateFromStore || new Date());
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment.');
    } finally {
      setLoading(false);
    }
  };

  const [showForm, setShowForm] = useState(false);

  const handleDeleteComment = async (index: number, item: string) => {
    const encodedDate = encodeDateForUrl(selectedDateFromStore || new Date());
    const comment = comments[index]; // actual full comment
    const lines = comment.split('\n');
    const updatedLines = lines.filter((line) => line.trim() !== item.trim());
    const updatedComment = updatedLines.join('\n');

    const payload: IUpdateComments = {
      ownerId: selectedLocation?.OwnerId,
      date: encodedDate,
      oldComment: comment,
      newComment: updatedComment === '' ? null : updatedComment,
    };
    try {
      setLoading(true);
      await operationAPI.updateComments(payload);
      showOrReplaceToast(
        'generalComment',
        translate('generalComment.generalCommentDelete'),
        'success',
      );
      fetchCalendarDetails(selectedDateFromStore || new Date());
      setShowForm(false);
    } catch (error) {
      console.error('Error deleting sub-comment:', error);
      toast.error('Failed to update comment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${bgColor} rounded-sm p-4 w-full mb-4`}>
      <div className="flex justify-between">
        {showHeading && (
          <h3 className="font-semibold text-base text-textColor mb-2 text-start">
            {translate('generalComment.generalComment')}
          </h3>
        )}
        <button>
          <div>
            <div className="block dark:hidden">
              <AccordianPlusIcon onClick={() => setShowForm(true)} />
            </div>
            <div className="hidden dark:block">
              <AccordianPlusIconDark onClick={() => setShowForm(true)} />
            </div>
          </div>
        </button>
      </div>
      <div className="border border-b border-customBorder-black mb-2"></div>
      {showForm ? (
        <>
          <div>
            <textarea
              className={`w-full border ${
                error ? 'border-red-500' : 'border-customBorder-border'
              } focus:outline-none focus:ring-0 focus:border-transparent text-text-customGrey rounded-md`}
              placeholder="Enter general comments"
              onChange={(e) => {
                handleCommentChange(e.target.value);
                if (error) setError(null);
              }}
              value={comment}
            ></textarea>

            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        </>
      ) : null}

      {loading || commentIsLoading ? (
        <p className="flex justify-center align-middle">
          <Loader />
        </p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-start text-textColorGray mb-4">
          {'No data available'}
        </p>
      ) : (
        <>
          {comments.map((item, index) => (
            <div className="mb-4" key={index}>
              <div className="flex items-center justify-between">
                {editingIndex === index ? (
                  <textarea
                    value={editedComment}
                    onChange={(e) => setEditedComment(e.target.value)}
                    className="w-full border border-customBorder-border focus:outline-none focus:ring-0 focus:border-transparent text-text-customGrey"
                  />
                ) : (
                  <p className="text-sm text-textColorGray">{item}</p>
                )}
                <div className="flex justify-around gap-4 ml-2">
                  {editingIndex === index ? (
                    <div>
                      <div className="block dark:hidden">
                        <CheckIcon
                          onClick={() => {
                            console.log(index); // <- This will now print the correct index
                            handleSaveEdit(index);
                          }}
                        />
                      </div>
                      <div className="hidden dark:block">
                        <CheckIconDark
                          onClick={() => {
                            console.log(index); // <- This will now print the correct index
                            handleSaveEdit(index);
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="block dark:hidden">
                        <EditIcon
                          onClick={() => handleEditClick(index, item)}
                        />
                      </div>
                      <div className="hidden dark:block">
                        <EditIconDark
                          onClick={() => handleEditClick(index, item)}
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="block dark:hidden">
                      <DeleteIcon
                        onClick={() => handleDeleteComment(index, item)}
                      />
                    </div>
                    <div className="hidden dark:block">
                      <DeleteIconDark
                        onClick={() => handleDeleteComment(index, item)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {showForm ? (
        <>
          <div className="flex justify-end gap-8">
            <div className="flex w-full justify-end">
              <ReusableButton
                variant="secondary"
                children={'Cancel'}
                icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
                onClick={() => {
                  setShowForm(false);
                  setComment('');
                  setError(null);
                }}
                className="w-auto bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
              />
              <ReusableButton
                icon={<CheckIcon />}
                children={'Confirm'}
                variant="primary"
                className="w-auto bg-background-backgroundColor"
                onClick={handleSubmit}
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default GeneralComment;
